import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import config from 'src/appConfig';
import {
  type ExportResponse,
  createMessageEventsExport,
  getMessageEventsExport
} from 'src/helpers/api/messageEvents';
import { downloadFromUrl } from 'src/helpers/downloading';
import { useAlert } from 'src/hooks';
import { useSparkPostMutation, useSparkPostQuery } from 'src/hooks/api';
import { exponentialBackoff, linearBackoff } from '../api/helpers/resiliency';

// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
const apiDateFormat = config.apiDateFormat as string;

const MIN_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 30000; // 30 seconds
const MIN_POLL_INTERVAL = 1000; // 1 second
const MAX_POLL_INTERVAL = 5000; // 5 seconds
const POLL_INTERVAL_STEP = 1000; // 1 second
const TIMEOUT_DURATION = 6 * 60 * 1000; // 6 minutes

const retryDelay = exponentialBackoff(MIN_RETRY_DELAY, MAX_RETRY_DELAY);
const pollInterval = linearBackoff(POLL_INTERVAL_STEP, MIN_POLL_INTERVAL, MAX_POLL_INTERVAL);

interface ExportFilters {
  [key: string]: unknown;
}

interface EventsSearchFilters extends ExportFilters {
  dateOptions: {
    from: string;
    to: string;
  };
}

interface UseAsyncCsvExportReturn {
  exportStatus: ExportResponse['status'] | null;
  isExporting: boolean;
  startExport: (filters: ExportFilters) => void;
}

export default function useAsyncCsvExport(): UseAsyncCsvExportReturn {
  const [exportId, setExportId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { showAlert } = useAlert();
  const refetchCountRef = useRef(0);

  const { mutate: createExport } = useSparkPostMutation(createMessageEventsExport, {
    onMutate: () => {
      setIsExporting(true);
      refetchCountRef.current = 0;
    },
    onSuccess: (data: ExportResponse) => {
      setExportId(data.id);
      setIsExporting(true);
      startTimeout();
    },
    onError: () => {
      setIsExporting(false);
      showAlert({
        type: 'error',
        message: 'Failed to create CSV file. Please try again.'
      });
    }
  });

  const { data } = useSparkPostQuery(() => getMessageEventsExport(exportId ?? ''), {
    enabled: !!exportId,
    retry: 2,
    retryDelay,
    refetchInterval: (data?: ExportResponse) => {
      if (!data || !isExporting) return false;
      return data.status === 'PENDING' || data.status === 'PROCESSING'
        ? pollInterval(refetchCountRef.current)
        : false;
    },
    onSuccess: (data: ExportResponse) => {
      if (data.status === 'COMPLETED') {
        handleExportComplete(data);
      } else if (data.status === 'FAILED') {
        handleExportFailed(data.error);
      }
    },
    onError: () => {
      handleExportFailed();
    },
    onSettled: () => {
      refetchCountRef.current++;
    }
  });

  const exportMetadata = data as ExportResponse | undefined;

  const startTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsExporting(false);
      setExportId(null);
    }, TIMEOUT_DURATION);
  }, []);

  const handleExportComplete = useCallback(
    (data: ExportResponse) => {
      setIsExporting(false);
      setExportId(null);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (data.file_url) {
        downloadFromUrl({
          name: `sparkpost-events-${data.id}.csv`,
          url: data.file_url
        });
      } else {
        showAlert({
          type: 'error',
          message: 'Export completed but download URL is not available.'
        });
      }
    },
    [showAlert]
  );

  const handleExportFailed = useCallback(
    (error?: string) => {
      setIsExporting(false);
      setExportId(null);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      showAlert({
        type: 'error',
        message: error ?? 'Export failed. Please try again.'
      });
    },
    [showAlert]
  );

  const startExport = useCallback(
    (filters: EventsSearchFilters) => {
      if (isExporting) {
        return;
      }

      createExport(formatFilters(filters));
    },
    [isExporting, createExport]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    exportStatus: exportMetadata?.status || null,
    isExporting,
    startExport
  };
}

function formatFilters(filters: EventsSearchFilters): ExportFilters {
  const { dateOptions, perPage, ...rest } = filters;
  const { from, to } = dateOptions;
  const params = {} as ExportFilters;

  if (from) {
    params.from = moment(from).utc().format(apiDateFormat);
  }

  if (to) {
    params.to = moment(to).utc().format(apiDateFormat);
  }

  for (const [key, value] of Object.entries(rest)) {
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }

    params[key] = value;
  }

  return params;
}
